/**
 * canvas-engine/presets/defaultTemplates.js
 *
 * OWN default-template scaffolding for the Enterprise Canvas Builder.
 *
 * PURPOSE: when an author opens the Canvas Builder for a domain WITHOUT an
 * existing template (a brand-new draft), the builder should not start blank —
 * it should immediately show that domain's DEFAULT pages, each pre-filled with
 * that domain's DEFAULT canvas sections (the transcribed default look). This
 * module provides:
 *
 *   - `normalizeDomainType(value)`  → maps the backend domain id
 *     (`EDUCATION` | `WAAS` | `FLEX_OFFICE` | `RETAIL`, any case / separators)
 *     to the registry family key (`education` | `waas` | `flexOffice` | `retail`).
 *   - `DOMAIN_PAGE_BLUEPRINTS`      → per-family ordered page list, each page an
 *     ordered list of Canvas_Preset section-type ids (== registry entry ids).
 *   - `createDefaultTemplate(domainType, opts)` → instantiates a full draft
 *     Template `{ domainType, domainFamily, pages: [{ key, pageKey, route,
 *     title, name, enabled, sections }] }`, where every section is a brand-new,
 *     fully-editable `customCanvas` section produced by `instantiatePreset`
 *     (deep-cloned canvas, fresh unique element ids — no shared references with
 *     the registry constant).
 *
 * The page → section structure is TRANSCRIBED from the Default_Source default
 * page builders (`create{Education,Waas,FlexOffice,Retail}DefaultPages`) as
 * plain DATA. Where the Default_Source home/info pages used a single legacy
 * component wrapper, the canvas-first default instead composes the equivalent
 * authored canvas sections so the page is editable end-to-end.
 *
 * CHROME PARITY: every seeded default page now includes the domain HEADER as
 * its FIRST section and the domain FOOTER as its LAST section, with the page's
 * body sections in between (`[header, ...body, footer]`). The chrome sections
 * are real, fully-editable `customCanvas` sections produced from the registered
 * chrome presets (`${family}-header` / `${family}-footer`, falling back to
 * `generic-header` / `generic-footer`), so they decompose to editable
 * logo / nav / social / text elements just like any other canvas section. If
 * neither the family nor the generic chrome preset resolves, that piece of
 * chrome is simply omitted for the page.
 *
 * ZERO-REUSE CONSTRAINT: imports ONLY sibling Canvas_Engine modules
 * (`canvasPresets`, `decompositionAdapters`, `model/canvasModel`). Nothing from
 * `admin-console-ui/src/modules/template-builder/` or `academy-ui/src/template/`.
 * No React, no DOM.
 */

import { instantiatePreset, getPreset } from './canvasPresets';
import { decomposeSection } from './decompositionAdapters';
import { normalizeCanvas } from '../model/canvasModel';

// ── Domain normalization ──────────────────────────────────────────────────────

/** The registry family keys this engine recognizes. */
const FAMILY_KEYS = ['education', 'waas', 'flexOffice', 'retail'];

/**
 * Map any backend domain id to its registry family key. Accepts the uppercase
 * backend ids (`EDUCATION`, `WAAS`, `FLEX_OFFICE`, `RETAIL`), already-normalized
 * camelCase keys, and assorted separators / casing. Returns '' when unknown.
 *
 * @param {string} value a domain id (any case / separators)
 * @returns {string} the family key, or '' when unrecognized
 */
export function normalizeDomainType(value) {
  if (typeof value !== 'string') return '';
  const raw = value.trim();
  if (!raw) return '';

  // Already a known family key (case-insensitive direct match).
  const direct = FAMILY_KEYS.find((k) => k.toLowerCase() === raw.toLowerCase());
  if (direct) return direct;

  // Collapse to alphanumerics for separator-insensitive matching
  // (FLEX_OFFICE / flex-office / Flex Office → flexoffice).
  const collapsed = raw.toLowerCase().replace(/[^a-z0-9]/g, '');
  switch (collapsed) {
    case 'education':
    case 'edu':
    case 'academy':
      return 'education';
    case 'waas':
    case 'avps':
    case 'avpstech':
      return 'waas';
    case 'flexoffice':
    case 'flex':
    case 'office':
      return 'flexOffice';
    case 'retail':
    case 'store':
    case 'shop':
      return 'retail';
    default:
      return '';
  }
}

// ── Per-domain default page blueprints ────────────────────────────────────────
//
// Each entry: { pageKey, route, title, sections: [<Canvas_Preset id>...] }.
// The preset ids equal the registry entry ids (== descriptor `sectionType`).
// Order matters: sections render top-to-bottom in this order on the page.

/**
 * DOMAIN_PAGE_BLUEPRINTS — the default pages + default section stacks per
 * family, transcribed from the Default_Source default-page builders.
 */
export const DOMAIN_PAGE_BLUEPRINTS = {
  // Education — 14 pages (closed set per R2.2). Routes/titles verbatim from
  // createEducationDefaultPages in educationTemplateDefaults.js.
  education: [
    {
      pageKey: 'home',
      route: '/',
      title: 'Education Default',
      sections: [
        'educationHero',
        'educationCategoryGrid',
        'educationFeatureGrid',
        'educationFeaturedCourses',
        'educationMissionVision',
        'educationJoinCommunity',
      ],
    },
    { pageKey: 'products', route: '/course', title: 'Courses', sections: ['educationProductsPage'] },
    { pageKey: 'courseDetail', route: '/course-detail', title: 'Course Detail', sections: ['educationCourseDetail'] },
    { pageKey: 'cart', route: '/cart', title: 'Cart', sections: ['educationCart'] },
    { pageKey: 'summary', route: '/order-history', title: 'Summary', sections: ['educationSummary'] },
    { pageKey: 'about', route: '/about', title: 'About', sections: ['educationAbout'] },
    { pageKey: 'aboutAcademy', route: '/about-academy', title: 'About Academy', sections: ['educationAbout'] },
    { pageKey: 'contact', route: '/contact', title: 'Contact', sections: ['educationContact'] },
    {
      pageKey: 'bridgingKnowledgeGap',
      route: '/BridgingTheKnowledgeGap',
      title: 'Bridging the Knowledge Gap',
      sections: ['educationBridgingKnowledgeGap'],
    },
    {
      pageKey: 'insightsEngagement',
      route: '/InsightsAndEngagement',
      title: 'Insights & Engagement',
      sections: ['educationInsightsEngagement'],
    },
    {
      pageKey: 'industryExperience',
      route: '/BuildIndustryExperience',
      title: 'Build Industry Experience',
      sections: ['educationIndustryExperience'],
    },
    {
      pageKey: 'groomingSkills',
      route: '/GroomingSkills',
      title: 'Technical & Soft Skills',
      sections: ['educationGroomingSkills'],
    },
    { pageKey: 'signIn', route: '/sign-in', title: 'Sign In', sections: ['educationAuthEntry'] },
    { pageKey: 'signUp', route: '/sign-up', title: 'Sign Up', sections: ['educationAuthEntry'] },
  ],

  waas: [
    {
      pageKey: 'home',
      route: '/',
      title: 'Home',
      sections: [
        'waasHero',
        'waasAboutStory',
        'waasServicesGrid',
        'waasTestimonials',
        'waasServiceDetail',
        'waasImageGallery',
        'waasNewsletterFooter',
      ],
    },
    {
      pageKey: 'about',
      route: '/about',
      title: 'About',
      sections: [
        'waasAboutStory',
        'waasInstructorSpotlight',
        'waasPhilosophy',
        'waasProcessSteps',
        'waasImageGallery',
        'waasNewsletterFooter',
      ],
    },
    {
      pageKey: 'services',
      route: '/services',
      title: 'Services',
      sections: [
        'waasServicesGrid',
        'waasServiceDetail',
        'waasPricingPlans',
        'waasSchedule',
        'waasFaq',
        'waasNewsletterFooter',
      ],
    },
    {
      pageKey: 'server',
      route: '/server',
      title: 'Server',
      sections: [
        'waasServiceDetail',
        'waasServicesGrid',
        'waasProcessSteps',
        'waasSchedule',
        'waasNewsletterFooter',
      ],
    },
    {
      pageKey: 'career',
      route: '/careers',
      title: 'Career',
      sections: [
        'waasCareerHero',
        'waasOpenRoles',
        'waasProcessSteps',
        'waasTestimonials',
        'waasNewsletterFooter',
      ],
    },
    {
      pageKey: 'contact',
      route: '/contactus',
      title: 'Contact',
      sections: ['waasContact', 'waasFaq', 'waasNewsletterFooter'],
    },
    { pageKey: 'signIn', route: '/sign-in', title: 'Sign In', sections: ['waasAuth', 'waasFaq'] },
    { pageKey: 'signUp', route: '/sign-up', title: 'Sign Up', sections: ['waasAuth', 'waasNewsletterFooter'] },
  ],

  // FlexOffice — 24 pages (closed set per R2.4). Routes/titles verbatim from
  // createFlexOfficeDefaultPages in flexOfficeTemplateDefaults.js.
  flexOffice: [
    {
      pageKey: 'home',
      route: '/',
      title: 'Flex Office',
      sections: [
        'flexOfficeHero',
        'flexOfficeAbout',
        'flexOfficeFeatures',
        'flexOfficeTimeline',
        'flexOfficeWorkingHours',
        'flexOfficeCta',
      ],
    },
    { pageKey: 'bookingHome', route: '/flex-home', title: 'Book Space', sections: ['flexOfficeBookingSearch'] },
    { pageKey: 'bookSpace', route: '/book-space', title: 'Book Space', sections: ['flexOfficeBookSpace'] },
    { pageKey: 'spaceTypes', route: '/type-of-spaces', title: 'Space Types', sections: ['flexOfficeSpaceTypes'] },
    { pageKey: 'exploreLocations', route: '/explore-locations', title: 'Explore Locations', sections: ['flexOfficeExploreLocations'] },
    { pageKey: 'bookSchedule', route: '/book-schedule', title: 'Book Schedule', sections: ['flexOfficeBookSchedule'] },
    { pageKey: 'choice', route: '/choice', title: 'Choose Space', sections: ['flexOfficeChoice'] },
    { pageKey: 'viewService', route: '/view-service', title: 'View Service', sections: ['flexOfficeViewService'] },
    { pageKey: 'services', route: '/our-services', title: 'Services', sections: ['flexOfficeServicesPage'] },
    { pageKey: 'serviceDetail', route: '/selected-product-details', title: 'Service Detail', sections: ['flexOfficeServiceDetail'] },
    { pageKey: 'features', route: '/our-features', title: 'Features', sections: ['flexOfficeFeatures'] },
    { pageKey: 'marketingFeatures', route: '/features', title: 'Features', sections: ['flexOfficeMarketingFeatures'] },
    { pageKey: 'confirmBook', route: '/confirm-book', title: 'Confirm Booking', sections: ['flexOfficeConfirmBook'] },
    { pageKey: 'reviewSchedule', route: '/review-schedule', title: 'Review Schedule', sections: ['flexOfficeReviewSchedule'] },
    { pageKey: 'bookings', route: '/my-bookings', title: 'My Bookings', sections: ['flexOfficeBookings'] },
    { pageKey: 'orders', route: '/my-orders', title: 'My Orders', sections: ['flexOfficeOrders'] },
    { pageKey: 'customerOrders', route: '/customer-orders', title: 'Customer Orders', sections: ['flexOfficeCustomerOrders'] },
    { pageKey: 'userCustomerOrders', route: '/user-profile/customer-orders', title: 'Customer Orders', sections: ['flexOfficeUserCustomerOrders'] },
    { pageKey: 'invoice', route: '/my-invoice', title: 'Invoices', sections: ['flexOfficeInvoice'] },
    { pageKey: 'invoiceDetail', route: '/my-invoice/:id', title: 'Invoice Detail', sections: ['flexOfficeInvoiceDetail'] },
    { pageKey: 'aboutFlexOffice', route: '/about-flexoffice', title: 'About FlexOffice', sections: ['flexOfficeAboutPage'] },
    { pageKey: 'contact', route: '/contact', title: 'Contact', sections: ['flexOfficeContact'] },
    { pageKey: 'signIn', route: '/sign-in', title: 'Sign In', sections: ['flexOfficeAuth'] },
    { pageKey: 'signUp', route: '/sign-up', title: 'Sign Up', sections: ['flexOfficeAuth'] },
  ],

  // Retail — 10 pages (closed set per R2.5). Routes/titles verbatim from
  // createRetailDefaultPages in retailTemplateDefaults.js.
  retail: [
    {
      pageKey: 'home',
      route: '/',
      title: 'Retail Store',
      sections: ['retailHeroCarousel', 'retailCategoryGrid', 'retailFeaturedProducts', 'retailPromoBanner'],
    },
    { pageKey: 'products', route: '/product', title: 'Products', sections: ['retailProductGrid'] },
    { pageKey: 'category', route: '/category', title: 'Category Products', sections: ['retailCategory'] },
    { pageKey: 'productDetail', route: '/product-info', title: 'Product Detail', sections: ['retailProductDetail'] },
    { pageKey: 'cart', route: '/cart', title: 'Cart', sections: ['retailCart'] },
    { pageKey: 'summary', route: '/order-history', title: 'Order Summary', sections: ['retailOrderSummary'] },
    { pageKey: 'about', route: '/about', title: 'About', sections: ['retailAbout'] },
    { pageKey: 'contact', route: '/contact', title: 'Contact', sections: ['retailContact'] },
    { pageKey: 'signIn', route: '/sign-in', title: 'Sign In', sections: ['retailAuth'] },
    { pageKey: 'signUp', route: '/sign-up', title: 'Sign Up', sections: ['retailAuth'] },
  ],
};

// ── Small pure helpers ────────────────────────────────────────────────────────

/** Trimmed string, or '' when not a usable string. */
function pickString(v) {
  return typeof v === 'string' ? v.trim() : '';
}

/** True for a non-null, non-array plain object. */
function isPlainObject(v) {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Fresh-id generator mirroring the one in `canvasPresets.js`. A module-local
 * monotonic counter (`ID_SEQUENCE`) guarantees uniqueness across EVERY call —
 * even within a single seeding pass — because each token embeds a distinct,
 * ever-increasing sequence value. The timestamp/random suffix only add
 * readability and cross-process spread.
 */
let ID_SEQUENCE = 0;
function freshId(prefix) {
  ID_SEQUENCE += 1;
  const seq = ID_SEQUENCE.toString(36);
  const time = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 10);
  return `${prefix || 'el'}-${time}-${seq}${rand}`;
}

/** Build a minimal empty home page (fallback when a domain has no blueprint). */
function makeEmptyHomePage() {
  return { key: 'home', pageKey: 'home', route: '/', title: 'Home', name: 'Home', enabled: true, sections: [] };
}

/**
 * Build a single seeded `customCanvas` section from a preset id.
 *
 * Two paths, both yielding a fully-editable `customCanvas` section that shares
 * no references with the registry constant and whose element ids are fresh and
 * unique:
 *
 *  - WITH `authVariant` (e.g. the `signUp` page): the Preset_Registry pre-builds
 *    each auth preset's canvas with the DEFAULT (signIn) content, so cloning the
 *    prebuilt canvas via `instantiatePreset` cannot produce the signUp variant.
 *    Instead we decompose the section fresh with `{ authVariant }` so the variant
 *    block is merged before layout, `normalizeCanvas` it, then rewrite every
 *    element id to a fresh token. This mirrors what `instantiatePreset` produces
 *    but with the requested variant applied.
 *  - WITHOUT `authVariant`: delegate to the existing `instantiatePreset` path,
 *    unchanged.
 *
 * Returns `undefined` when `presetId` does not resolve to a registered preset so
 * the caller can seed the fallback Canvas_Shell instead (R2.7, R3.9, R6.7).
 *
 * @param {string} presetId a Canvas_Preset id
 * @param {string} sectionId the deterministic section id to stamp
 * @param {object} [opts]
 * @param {string} [opts.authVariant] e.g. `'signUp'` to select the auth variant
 * @returns {object|undefined} a new `customCanvas` section, or `undefined`
 */
function buildSectionFromPreset(presetId, sectionId, opts = {}) {
  const authVariant = pickString(opts && opts.authVariant);

  // Unregistered ids never resolve to a real preset — let the caller fall back.
  if (!presetId || !getPreset(presetId)) return undefined;

  if (!authVariant) {
    const section = instantiatePreset(presetId, { id: sectionId });
    if (!section) return undefined;
    section.sectionId = section.id; // keep both id shapes addressable
    return section;
  }

  // Auth-variant path: decompose fresh with the variant merged, then normalize
  // and re-id every element so this section shares no references with the
  // registry constant and collides with nothing.
  const canvas = normalizeCanvas(decomposeSection(presetId, { authVariant }));
  if (Array.isArray(canvas.elements)) {
    for (let i = 0; i < canvas.elements.length; i += 1) {
      const el = canvas.elements[i];
      if (isPlainObject(el)) el.id = freshId(el.type || 'el');
    }
  }
  return {
    id: sectionId,
    sectionId,
    type: 'customCanvas',
    presetId,
    content: { canvas },
  };
}

/**
 * Build a FALLBACK Canvas_Shell for a blueprint-declared page whose preset id
 * could not be resolved/registered (R2.7, R3.9). The shell is a minimal,
 * fully-editable `customCanvas` section containing ONE editable `text` element
 * carrying the page title, so the declared page still yields a Canvas_Section.
 *
 * @param {string} presetId the unresolved preset id (kept for traceability)
 * @param {string} sectionId the deterministic section id to stamp
 * @param {object} page the blueprint page (for its title)
 * @returns {object} a new `customCanvas` fallback section
 */
function buildFallbackShell(presetId, sectionId, page) {
  const heading = pickString(page && page.title);
  const canvas = normalizeCanvas({
    elements: [{ type: 'text', x: 0, y: 0, width: 1040, height: 80, content: { heading } }],
  });
  if (Array.isArray(canvas.elements)) {
    for (let i = 0; i < canvas.elements.length; i += 1) {
      const el = canvas.elements[i];
      if (isPlainObject(el)) el.id = freshId(el.type || 'el');
    }
  }
  return {
    id: sectionId,
    sectionId,
    type: 'customCanvas',
    presetId: presetId || undefined,
    content: { canvas },
  };
}

/**
 * Resolve and build a domain CHROME section (header or footer) as a real,
 * fully-editable `customCanvas` section.
 *
 * The chrome preset id is resolved by preference: the family-specific chrome
 * (`${family}-${part}`, e.g. `education-header`, `flexOffice-footer`) is used
 * when registered; otherwise it falls back to the neutral `generic-${part}`
 * preset. The section itself is produced through the same
 * `buildSectionFromPreset` (deep-cloned `instantiatePreset` path, no
 * `authVariant`) used for body sections, so the chrome decomposes into editable
 * logo / nav / social / text Canvas_Elements.
 *
 * Returns `undefined` only when neither the family nor the generic chrome
 * preset resolves, so the caller can simply omit that chrome from the page.
 *
 * @param {string} family the registry family key (may be '' for unknown domains)
 * @param {('header'|'footer')} part which chrome part to build
 * @param {string} sectionId the deterministic section id to stamp
 * @returns {object|undefined} a new `customCanvas` chrome section, or `undefined`
 */
function buildChromeSection(family, part, sectionId) {
  const familyId = family ? `${family}-${part}` : '';
  const genericId = `generic-${part}`;
  const presetId = familyId && getPreset(familyId) ? familyId : (getPreset(genericId) ? genericId : '');
  if (!presetId) return undefined;
  return buildSectionFromPreset(presetId, sectionId);
}

// ── Public API ─────────────────────────────────────────────────────────────────

/**
 * Return the default page blueprint list for a domain (normalized), or `[]`
 * when the domain is unknown.
 *
 * @param {string} domainType a backend domain id or family key
 * @returns {Array} the blueprint pages (shared constant — do not mutate)
 */
export function getDomainPageBlueprint(domainType) {
  const family = normalizeDomainType(domainType);
  return (family && DOMAIN_PAGE_BLUEPRINTS[family]) || [];
}

/**
 * Create a brand-new default draft Template for a domain, pre-filled with that
 * domain's default pages and default canvas sections.
 *
 * Every section is instantiated via the own `buildSectionFromPreset`, so it is
 * a fully-editable `customCanvas` section with a deep-cloned canvas and fresh
 * unique element ids (no references shared with the registry). Section ids are
 * deterministic (`${family}-${pageKey}-${n}`) so the same default scaffold is
 * stable across opens (R2.9, R8.5). For the `signUp` page the auth section is
 * decomposed with `authVariant: 'signUp'` so it shows the sign-up variant.
 *
 * Each seeded page is wrapped in domain CHROME: the domain header is composed
 * as the FIRST section and the domain footer as the LAST section, with the
 * page's body sections in between (`[header, ...body, footer]`). Chrome uses
 * deterministic ids (`${family}-${pageKey}-header` / `-footer`) that never
 * collide with body ids, resolves the family chrome preset
 * (`${family}-header` / `${family}-footer`) with a `generic-*` fallback, and is
 * omitted only when no chrome preset resolves.
 *
 * A blueprint-declared preset id that does not resolve to a registered preset
 * no longer silently skips the section — instead a fallback Canvas_Shell (one
 * editable `text` element carrying the page title) is seeded so each declared
 * page always yields a Canvas_Section, and the unresolved id is logged
 * (R2.7, R3.9).
 *
 * Seeding each page is wrapped defensively: if seeding a page throws, the
 * scaffold halts seeding further pages for that family and returns the pages
 * built so far PLUS an `error` indication identifying the `domainFamily` and the
 * failing `pageKey` (R2.8). `createDefaultTemplate` never throws on entry.
 *
 * When the domain has no blueprint (unknown domain), a single empty Home page
 * is returned so the builder still opens with a usable, editable draft (R7.6).
 *
 * @param {string} domainType a backend domain id (`EDUCATION` | `WAAS` |
 *   `FLEX_OFFICE` | `RETAIL`) or a family key
 * @param {object} [opts]
 * @param {string} [opts.domainType] preferred raw domain id to stamp on the
 *   draft (defaults to the passed `domainType`) for backend compatibility
 * @returns {object} a draft Template `{ domainType, domainFamily, pages }`,
 *   optionally with an `error` indication when seeding halted partway
 */
export function createDefaultTemplate(domainType, opts = {}) {
  const family = normalizeDomainType(domainType);
  const rawDomain = pickString(opts && opts.domainType) || pickString(domainType);
  const blueprint = (family && DOMAIN_PAGE_BLUEPRINTS[family]) || null;

  if (!blueprint || blueprint.length === 0) {
    // Unknown domain: a single editable Home page. It carries no family body
    // sections, but we still wrap it in generic chrome (header first, footer
    // last) when the generic chrome presets resolve, so even the fallback draft
    // opens with editable header/footer chrome (R7.6 preserved).
    const home = makeEmptyHomePage();
    const genericHeader = buildChromeSection('', 'header', 'home-header');
    const genericFooter = buildChromeSection('', 'footer', 'home-footer');
    const homeSections = [];
    if (genericHeader) homeSections.push(genericHeader);
    if (genericFooter) homeSections.push(genericFooter);
    home.sections = homeSections;
    return {
      domainType: rawDomain || undefined,
      domainFamily: family || undefined,
      pages: [home],
    };
  }

  const pages = [];
  for (let p = 0; p < blueprint.length; p += 1) {
    const page = blueprint[p];
    try {
      const sectionTypes = Array.isArray(page.sections) ? page.sections : [];
      // Select the auth sign-up variant ONLY for the signUp page; every other
      // page (including signIn) uses the default content (no variant).
      const authVariant = page.pageKey === 'signUp' ? 'signUp' : undefined;
      const body = [];
      for (let i = 0; i < sectionTypes.length; i += 1) {
        const presetId = pickString(sectionTypes[i]);
        const sectionId = `${family}-${page.pageKey}-${i + 1}`;
        let section = buildSectionFromPreset(presetId, sectionId, { authVariant });
        if (!section) {
          // Unresolved/unregistered preset id → seed a fallback Canvas_Shell so
          // the declared page still yields a Canvas_Section (R2.7, R3.9).
          // eslint-disable-next-line no-console
          console.warn(
            `[canvas-engine] defaultTemplates: unresolved preset id "${presetId}" ` +
              `for page "${page.pageKey}" (family "${family}") — seeding fallback shell.`,
          );
          section = buildFallbackShell(presetId, sectionId, page);
        }
        body.push(section);
      }
      // Compose the final page as [header, ...body, footer]. Chrome sections use
      // deterministic ids that never collide with body ids
      // (`${family}-${pageKey}-${n}`): `${family}-${pageKey}-header` /
      // `${family}-${pageKey}-footer`. Omit a chrome piece if it does not
      // resolve (neither family nor generic preset registered).
      const header = buildChromeSection(family, 'header', `${family}-${page.pageKey}-header`);
      const footer = buildChromeSection(family, 'footer', `${family}-${page.pageKey}-footer`);
      const sections = [];
      if (header) sections.push(header);
      for (let i = 0; i < body.length; i += 1) sections.push(body[i]);
      if (footer) sections.push(footer);
      pages.push({
        key: page.pageKey,
        pageKey: page.pageKey,
        route: page.route,
        title: page.title,
        name: page.title,
        enabled: true,
        sections,
      });
    } catch (err) {
      // Partial-failure halt (R2.8): stop seeding further pages, return the
      // pages built so far plus an error indication. Never throw on entry.
      return {
        domainType: rawDomain || undefined,
        domainFamily: family || undefined,
        pages,
        error: {
          domainFamily: family || undefined,
          pageKey: page && page.pageKey,
          message: (err && err.message) || String(err),
        },
      };
    }
  }

  return {
    domainType: rawDomain || undefined,
    domainFamily: family || undefined,
    pages,
  };
}

export default createDefaultTemplate;
